import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Alert, Loader } from '../common';

interface PhoneAuthFormProps {
  onSuccess: (phone: string, expiresAt: string) => void;
}

export const PhoneAuthForm: React.FC<PhoneAuthFormProps> = ({ onSuccess }) => {
  const { requestToken, error } = useAuth();
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isValidPhone, setIsValidPhone] = useState(false);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Accepte uniquement les chiffres et le préfixe +237
    const value = e.target.value.replace(/[^\d+]/g, '');
    setPhone(value);
    setFormError(null);
  };

  // Valider le format du numéro de téléphone
  useEffect(() => {
    // Formats valides: 6XXXXXXXX, 7XXXXXXXX, 8XXXXXXXX, 9XXXXXXXX
    // ou avec préfixe: 237XXXXXXXXX, +237XXXXXXXXX
    const phoneRegex = /^(\+?237)?[6-9][0-9]{8}$/;
    setIsValidPhone(phoneRegex.test(phone));
  }, [phone]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSuccessMessage(null);
    
    // Validation basique
    if (!phone) {
      setFormError('Veuillez entrer votre numéro de téléphone');
      return;
    }

    try {
      setIsLoading(true);
      // Le formatage du numéro est géré dans le contexte d'authentification
      // via authService.formatPhoneNumber
      const response = await requestToken(phone);
      
      if (response.status === 'success') {
        setSuccessMessage(response.message);
        onSuccess(response.data.phone, response.data.expires_at);
      }
    } catch (error) {
      if (error instanceof Error) {
        setFormError(error.message);
      } else {
        setFormError('Une erreur est survenue lors de la demande de token');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg px-8 pt-6 pb-8 mb-4">
        
        {(formError || error) && (
          <div className="mb-4">
            <Alert 
              type="error" 
              message={formError || error || ''} 
              onClose={() => setFormError(null)}
            />
          </div>
        )}
        
        {successMessage && (
          <div className="mb-4">
            <Alert 
              type="success" 
              message={successMessage} 
              onClose={() => setSuccessMessage(null)}
            />
          </div>
        )}
        
        <div className="mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="phone">
            Numéro de téléphone
          </label>
          <div className="relative">
            <input
              id="phone"
              type="tel"
              className="shadow appearance-none border rounded w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-secondary"
              placeholder="6XXXXXXXX ou 2376XXXXXXXX"
              value={phone}
              onChange={handlePhoneChange}
              disabled={isLoading}
            />
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Vous recevrez un SMS avec un lien d'authentification
          </p>
        </div>
        
        <div className="flex items-center justify-center">
          <button
            type="submit"
            className={`${isValidPhone ? 'bg-primary hover:bg-yellow-500' : 'bg-gray-300 cursor-not-allowed'} text-tertiary font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full flex justify-center items-center`}
            disabled={isLoading || !isValidPhone}
          >
            {isLoading ? <Loader size="small" color="tertiary" /> : 'Recevoir le lien'}
          </button>
        </div>
      </form>
    </div>
  );
};
